import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';

import { HandoffCheckStatus, IHandoffCheck } from 'components/Intelligence/HandoffCheckList';
import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { formatNumber } from 'utils/formatters';

import useCardData from '../data/useCardData';
import { CardDataStateKind, CardMetric, IntelligenceFilters } from '../types';

export type GrowthPlannerHandoffStatus = 'idle' | 'sending' | 'success' | 'failed';

export interface IHandoffMetricRow {
  key: string;
  name: string;
  /** Pre-formatted for `HandoffMetricOption`; the row renders strings, never numbers. */
  total: string;
  eligible: string;
  confidence?: string;
  /** Present on a row that cannot be handed off. The row stays listed and states the reason. */
  unavailableReason?: string;
  /** The population behind `eligible`, kept numeric so the panel can total a selection. */
  eligibleCustomers: number;
}

export interface IGrowthPlannerHandoffRequest {
  cardKey: string;
  metricKeys: string[];
  customers: number;
}

export interface IGrowthPlannerHandoffReceipt {
  handoffId: string;
  /** ISO-8601 including a time component — Safari rejects a bare date. */
  sentAt: string;
  metricsSent: number;
  customers: number;
}

export type GrowthPlannerHandoffSender = (
  request: IGrowthPlannerHandoffRequest
) => Promise<IGrowthPlannerHandoffReceipt>;

export interface IGrowthPlannerHandoffOptions {
  cardKey: string;
  filters?: IntelligenceFilters;
  /**
   * `useCardVisibility().canSendToGrowthPlanner`, threaded in rather than re-derived: the CTA gate
   * has one resolver and a second `usePermissions` call here would be a second source of truth.
   */
  permitted?: boolean;
  sender?: GrowthPlannerHandoffSender;
}

export interface IGrowthPlannerHandoffResult {
  status: GrowthPlannerHandoffStatus;
  metrics: IHandoffMetricRow[];
  selectedKeys: string[];
  toggleMetric: (key: string) => void;
  checks: IHandoffCheck[];
  canContinue: boolean;
  selectedCount: number;
  /** Total customers behind the current selection — the figure the panel footer states. */
  eligibleCustomers: number;
  receipt: IGrowthPlannerHandoffReceipt | null;
  errorRef: string | null;
  send: () => void;
}

export const HANDOFF_SEND_DELAY_MS = 900;

const RESTRICTED_STATE_KEYS: Partial<Record<CardDataStateKind, string>> = {
  technicalError: 'TechnicalError',
  insufficientData: 'InsufficientData',
  notConfigured: 'NotConfigured',
  awaitingData: 'AwaitingData',
  permissionRestricted: 'PermissionRestricted',
  consentRestricted: 'ConsentRestricted',
  noMatchingCustomers: 'NoMatchingCustomers'
};

/**
 * NOTE: no Growth Planner handoff endpoint exists yet (plan § AF-4), so the seam resolves locally
 * against the selection it was handed. Swapping it for `API.post(...)` is a change to this function
 * and to nothing else — every consumer already awaits a promise of a receipt. PLA-8815.
 */
export const sendGrowthPlannerHandoff: GrowthPlannerHandoffSender = request =>
  new Promise(resolve => {
    setTimeout(
      () =>
        resolve({
          handoffId: `GP-${Date.now().toString().slice(-6)}`,
          sentAt: moment().toISOString(),
          metricsSent: request.metricKeys.length,
          customers: request.customers
        }),
      HANDOFF_SEND_DELAY_MS
    );
  });

const formatMetricReading = (metric: CardMetric): string =>
  metric.value === null
    ? formatNumber(null)
    : `${formatNumber(metric.value)}${metric.format === 'percent' ? '%' : ''}`;

const errorReferenceFrom = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : 'GP-UNKNOWN';

/**
 * The Growth Planner slide-out's whole state: which metrics can be handed off, which are chosen,
 * how the handoff checks read, and whether `Continue` may fire. It reads the card through the SAME
 * `useCardData` entry the originating card renders from — the FR requires one confidence
 * calculation behind both, and a second handoff fixture would drift from the card silently.
 * PLA-8815.
 */
export const useGrowthPlannerHandoff = ({
  cardKey,
  filters,
  permitted = true,
  sender = sendGrowthPlannerHandoff
}: IGrowthPlannerHandoffOptions): IGrowthPlannerHandoffResult => {
  const { t } = useMultiLanguageTranslation();
  const { data, state } = useCardData(cardKey, filters, permitted);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [status, setStatus] = useState<GrowthPlannerHandoffStatus>('idle');
  const [receipt, setReceipt] = useState<IGrowthPlannerHandoffReceipt | null>(null);
  const [errorRef, setErrorRef] = useState<string | null>(null);

  // The panel can be closed mid-send, which removes it from the tree before the seam settles.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const restrictionKey = permitted
    ? RESTRICTED_STATE_KEYS[state.kind]
    : RESTRICTED_STATE_KEYS.permissionRestricted;
  const restrictionReason = restrictionKey
    ? t(`${TCustomerInsights}.DataStates.${restrictionKey}`)
    : undefined;
  const cardConfidence = data?.confidence;

  const metrics = useMemo<IHandoffMetricRow[]>(() => {
    const rows = data?.metrics ?? [];

    // NOTE: a handoff transfers a customer POPULATION, so only count-format metrics are listed. A
    // rate has no population of its own in this payload and the FR forbids inventing one, so the
    // rate rows are left out rather than shown with a fabricated eligible figure. PLA-8815.
    return rows
      .filter(metric => metric.format === 'count')
      .map(metric => {
        const reading = formatMetricReading(metric);
        const unavailableReason =
          restrictionReason ??
          (metric.value === null
            ? t(`${TCustomerInsights}.DataStates.InsufficientData`)
            : undefined);

        return {
          key: metric.key,
          name: metric.label,
          total: reading,
          eligible: unavailableReason ? formatNumber(null) : reading,
          confidence: cardConfidence ? `${formatNumber(cardConfidence.score)}%` : undefined,
          unavailableReason,
          eligibleCustomers: unavailableReason || metric.value === null ? 0 : metric.value
        };
      });
  }, [cardConfidence, data, restrictionReason, t]);

  const availableKeys = useMemo(
    () => metrics.filter(metric => !metric.unavailableReason).map(metric => metric.key),
    [metrics]
  );
  // A primitive stands in for the array in the effect below: the array identity changes on every
  // resolve, the signature only when the eligible set actually does.
  const availableKeySignature = availableKeys.join('|');

  useEffect(() => {
    const keys = availableKeySignature ? availableKeySignature.split('|') : [];

    setSelectedKeys(previous => {
      const retained = previous.filter(key => keys.includes(key));

      // Preselected when the card exposes exactly one eligible metric, so the single-metric card
      // needs no click before `Continue` becomes reachable.
      if (retained.length === 0 && keys.length === 1) {
        return keys;
      }

      return retained.length === previous.length ? previous : retained;
    });
  }, [availableKeySignature]);

  const eligibleCustomers = useMemo(
    () =>
      metrics
        .filter(metric => selectedKeys.includes(metric.key))
        .reduce((total, metric) => total + metric.eligibleCustomers, 0),
    [metrics, selectedKeys]
  );

  const cardEligibleCustomers = useMemo(
    () => metrics.reduce((total, metric) => total + metric.eligibleCustomers, 0),
    [metrics]
  );

  const confidenceStatus: HandoffCheckStatus = (() => {
    if (cardConfidence === undefined) {
      return 'warning';
    }

    if (cardConfidence === null) {
      return 'failed';
    }

    // `insufficient` joins `low` rather than falling through to `passed`: both sit under the
    // catalogue's 0.60 handoff minimum, and the band was widened to carry the fourth one. PLA-8828.
    return cardConfidence.band === 'low' || cardConfidence.band === 'insufficient'
      ? 'warning'
      : 'passed';
  })();

  const consentPassed = state.kind !== 'consentRestricted';
  const permissionPassed = permitted && state.kind !== 'permissionRestricted';
  const updatedAt = data?.updatedAt ?? null;

  // Parsed in strict ISO mode rather than through `new Date(...)`, which Safari rejects without a
  // time component (conventions/components/cross-browser.md § 3).
  const freshnessLabel = useMemo(() => {
    if (!updatedAt) {
      return null;
    }

    const stamp = moment(updatedAt, moment.ISO_8601, true);

    return stamp.isValid() ? stamp.format('DD MMM YYYY') : null;
  }, [updatedAt]);

  const checks = useMemo<IHandoffCheck[]>(() => {
    const survivors = formatNumber(cardEligibleCustomers);

    // The rail is the handoff funnel: each check carries the population that survives it, which is
    // the reading the card's own gate fixture uses.
    return [
      {
        label: t(`${TCustomerInsights}.Handoff.Checks.Population`),
        value: survivors,
        status: cardEligibleCustomers > 0 ? 'passed' : 'failed'
      },
      {
        label: t(`${TCustomerInsights}.Handoff.Checks.Confidence`),
        value: cardConfidence ? `${formatNumber(cardConfidence.score)}%` : formatNumber(null),
        status: confidenceStatus
      },
      {
        label: t(`${TCustomerInsights}.Handoff.Checks.Consent`),
        value: consentPassed ? survivors : formatNumber(0),
        status: consentPassed ? 'passed' : 'failed'
      },
      {
        label: t(`${TCustomerInsights}.Handoff.Checks.Permission`),
        value: permissionPassed ? survivors : formatNumber(0),
        status: permissionPassed ? 'passed' : 'failed'
      },
      {
        label: t(`${TCustomerInsights}.Handoff.Checks.Freshness`),
        value: freshnessLabel ?? formatNumber(null),
        status: freshnessLabel ? 'passed' : 'warning'
      }
    ];
  }, [
    cardConfidence,
    cardEligibleCustomers,
    confidenceStatus,
    consentPassed,
    freshnessLabel,
    permissionPassed,
    t
  ]);

  // Freshness is deliberately absent: a stale reading is worth surfacing but must not block a
  // handoff, which is what the third status value is for.
  const mandatoryFailed =
    cardEligibleCustomers === 0 ||
    confidenceStatus === 'failed' ||
    !consentPassed ||
    !permissionPassed;

  const canContinue = selectedKeys.length > 0 && !mandatoryFailed && status !== 'sending';

  const toggleMetric = useCallback((key: string) => {
    setSelectedKeys(previous =>
      previous.includes(key) ? previous.filter(selected => selected !== key) : [...previous, key]
    );
  }, []);

  const send = useCallback(() => {
    if (!canContinue) {
      return;
    }

    setStatus('sending');
    setErrorRef(null);

    sender({ cardKey, metricKeys: selectedKeys, customers: eligibleCustomers })
      .then(sent => {
        if (!mounted.current) {
          return;
        }

        setReceipt(sent);
        setStatus('success');
      })
      .catch(error => {
        if (!mounted.current) {
          return;
        }

        setErrorRef(errorReferenceFrom(error));
        setStatus('failed');
      });
  }, [canContinue, cardKey, eligibleCustomers, selectedKeys, sender]);

  return {
    status,
    metrics,
    selectedKeys,
    toggleMetric,
    checks,
    canContinue,
    selectedCount: selectedKeys.length,
    eligibleCustomers,
    receipt,
    errorRef,
    send
  };
};

export default useGrowthPlannerHandoff;
